import { Link } from 'react-router-dom';

import { useMediaUrl } from '../media';

/**
 * The blocks a global section is made of.
 *
 * ── STRUCTURED PIECES, NOT A RICH-TEXT FIELD ─────────────────────────────────────────
 *
 * A footer could have been one block with an HTML body, and that is the shortest path
 * back to storing markup in a database. Instead it is a small set of named shapes, each
 * with typed props the validator understands. The cost is that a tenant cannot make an
 * arbitrary layout; the benefit is that nothing published here can execute, and that a
 * theme can restyle all of it without touching a single stored value.
 *
 * ── COLOUR COMES FROM THE THEME, NOT FROM PROPS ──────────────────────────────────────
 *
 * §68/§69. A block takes a VARIANT — default, quiet, accent — and the theme decides what
 * each one looks like. If blocks carried colours, rebranding an organization would mean
 * editing every section it had ever published, and half of them would be missed.
 */

/** A link authored in CMS content. Internal paths only; the validator enforces it. */
function InternalLink({ path, label }: { path: string; label: string }) {
  // Defence in depth: the engine refuses anything that is not an internal path at save
  // and at publish. This is the last place before an href, and a stored value that
  // somehow bypassed both must not become one.
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
    return <span className="cms-sec__link is-invalid">{label}</span>;
  }
  return <Link className="cms-sec__link" to={path}>{label}</Link>;
}

export function FooterColumns({
  heading = '', legalLine = '', showVoletoCredit = true, children,
}: {
  heading?: string;
  legalLine?: string;
  showVoletoCredit?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <footer className="cms-sec cms-sec--footer">
      {heading && <h2 className="cms-sec__heading">{heading}</h2>}
      <div className="cms-sec__columns">{children}</div>
      {(legalLine || showVoletoCredit) && (
        <div className="cms-sec__legal">
          {legalLine && <p>{legalLine}</p>}
          {showVoletoCredit && <p className="cms-sec__credit">Powered by Voleto</p>}
        </div>
      )}
    </footer>
  );
}

export function LinkList({
  heading = '', links = [],
}: { heading?: string; links?: { label: string; path: string }[] }) {
  const items = Array.isArray(links) ? links : [];
  return (
    <nav className="cms-sec__list" aria-label={heading || undefined}>
      {heading && <h3 className="cms-sec__subheading">{heading}</h3>}
      <ul>
        {items.map((link, i) => (
          <li key={`${link?.path ?? ''}-${i}`}>
            <InternalLink path={String(link?.path ?? '')} label={String(link?.label ?? '')} />
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function AnnouncementBar({
  message = '', tone = 'default', linkLabel = '', linkPath = null,
}: {
  message?: string;
  tone?: string;
  linkLabel?: string;
  linkPath?: string | null;
}) {
  if (!message) return null;
  // The tone is matched against a known set rather than interpolated into a class name.
  // `cms-sec--${tone}` with an unvalidated tone is a class name from a document.
  const variant = tone === 'muted' || tone === 'accent' ? tone : 'default';
  return (
    <aside className={`cms-sec cms-sec--announcement is-${variant}`} role="note">
      <p>{message}</p>
      {linkPath && linkLabel && <InternalLink path={linkPath} label={linkLabel} />}
    </aside>
  );
}

export function SponsorStrip({
  heading = '', logos = [],
}: { heading?: string; logos?: { mediaAssetId: string; label?: string }[] }) {
  const items = Array.isArray(logos) ? logos.filter((l) => l?.mediaAssetId) : [];
  if (items.length === 0 && !heading) return null;
  return (
    <section className="cms-sec cms-sec--sponsors">
      {heading && <h2 className="cms-sec__heading">{heading}</h2>}
      <ul className="cms-sec__logos">
        {items.map((logo, i) => (
          <li key={`${logo.mediaAssetId}-${i}`}>
            <SponsorLogo assetId={logo.mediaAssetId} label={logo.label ?? ''} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A sponsor logo.
 *
 * The document holds an ASSET ID, and the engine's media resolver turns it into a URL
 * after authorizing the request — the same path the hero uses, rather than a second one.
 * So a logo that moves bucket, becomes private or is deleted does not leave a published
 * footer pointing at a dead address.
 *
 * An unresolvable asset renders its label instead of a broken image. Object storage is
 * not configured in every environment, and a footer full of broken-image icons is a
 * worse answer than a footer full of sponsor names.
 */
function SponsorLogo({ assetId, label }: { assetId: string; label: string }) {
  const url = useMediaUrl(assetId);
  if (!url) return <span className="cms-sec__logo-fallback">{label}</span>;
  return <img className="cms-sec__logo" src={url} alt={label} loading="lazy" />;
}
